import React, { useState, useEffect, useRef } from "react";
import featureDetails from "./Feature_details.json";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoiceDollar,
  faCalculator,
  faShieldAlt,
  faCogs,
  faFileInvoice,
  faUniversity,
  faBoxes,
  faChartBar,
  faBalanceScale,
  faNetworkWired,
  faMoneyBillWave,
  faRocket,
  faArrowRight,
  faBolt,
  faCircleCheck,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import "./Features.css";

// ── Feature categories with metadata ──────────────────
const CATEGORIES = [
  {
    key: "Invoicing",
    icon: faFileInvoiceDollar,
    label: "Invoicing",
    color: "blue",
    desc: "GST-compliant billing & invoice management",
    cardIcon: faFileInvoiceDollar,
  },
  {
    key: "Accounting",
    icon: faCalculator,
    label: "Accounting",
    color: "purple",
    desc: "Double-entry bookkeeping & financial reports",
    cardIcon: faCalculator,
  },
  {
    key: "Security",
    icon: faShieldAlt,
    label: "Security",
    color: "coral",
    desc: "Enterprise-grade data protection",
    cardIcon: faShieldAlt,
  },
  {
    key: "Business Operation",
    icon: faCogs,
    label: "Operations",
    color: "teal",
    desc: "Automate & streamline business workflows",
    cardIcon: faCogs,
  },
  {
    key: "E-Invoicing",
    icon: faFileInvoice,
    label: "E-Invoicing",
    color: "green",
    desc: "Real-time e-invoice & e-way bill generation",
    cardIcon: faFileInvoice,
  },
  {
    key: "Banking",
    icon: faUniversity,
    label: "Banking",
    color: "amber",
    desc: "Multi-bank reconciliation & fund management",
    cardIcon: faUniversity,
  },
  {
    key: "Inventory",
    icon: faBoxes,
    label: "Inventory",
    color: "pink",
    desc: "Multi-warehouse stock & barcode tracking",
    cardIcon: faBoxes,
  },
  {
    key: "Reports",
    icon: faChartBar,
    label: "Reports",
    color: "blue",
    desc: "Financial statements & custom analytics",
    cardIcon: faChartBar,
  },
  {
    key: "GST",
    icon: faBalanceScale,
    label: "GST",
    color: "green",
    desc: "Automated GST filing & reconciliation",
    cardIcon: faBalanceScale,
  },
  {
    key: "Connected Service",
    icon: faNetworkWired,
    label: "Connected",
    color: "purple",
    desc: "API integrations & compliance portals",
    cardIcon: faNetworkWired,
  },
  {
    key: "Cash Flow",
    icon: faMoneyBillWave,
    label: "Cash Flow",
    color: "teal",
    desc: "Real-time cash tracking & forecasting",
    cardIcon: faMoneyBillWave,
  },
];

// Color class for cards based on category index
const CARD_COLORS = [
  "ft-cblue",
  "ft-cpurple",
  "ft-ccoral",
  "ft-cteal",
  "ft-cgreen",
  "ft-camber",
  "ft-cpink",
  "ft-cblue",
  "ft-cgreen",
  "ft-cpurple",
  "ft-cteal",
];

const Features = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const tabsRef = useRef(null);
  const contentRef = useRef(null);

  // Total feature count
  const totalFeatures = Object.values(featureDetails).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("ft-visible");
        });
      },
      { threshold: 0.1 }
    );
    document
      .querySelectorAll(".ft-reveal")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Sticky tab scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll active tab into view
  useEffect(() => {
    if (tabsRef.current) {
      const activeBtn = tabsRef.current.querySelector(".ft-tab.active");
      if (activeBtn) {
        activeBtn.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeTab]);

  const activeCat = CATEGORIES[activeTab];
  const activeFeatures = featureDetails[activeCat.key] || [];
  const cardColor = CARD_COLORS[activeTab];

  return (
    <div className="ft-root">
      {/* ══ HERO ═══════════════════════════════════════ */}
      <section className="ft-hero">
        <div className="ft-hero-grid" />
        <div className="ft-orb ft-orb-1" />
        <div className="ft-orb ft-orb-2" />
        <div className="ft-orb ft-orb-3" />

        <div className="ft-hero-inner">
          <div className="ft-hero-badge">
            <FontAwesomeIcon icon={faBolt} />
            All-in-One ERP Platform
          </div>

          <h1 className="ft-hero-h1">
            Every Feature You Need to{" "}
            <span className="ft-grad">Run Your Business</span>
          </h1>

          <p className="ft-hero-sub">
            From invoicing to inventory, GST compliance to cash flow — Auditus-iERP
            gives you a complete toolkit built for Indian businesses.
          </p>

          <div className="ft-hero-pills">
            <div className="ft-pill">
              <span className="ft-pill-num">15+</span>
              Modules
            </div>
            <div className="ft-pill">
              <span className="ft-pill-num">150+</span>
              Features
            </div>
            <div className="ft-pill">
              <span className="ft-pill-num">100%</span>
              GST Compliant
            </div>
            <div className="ft-pill">
              <span className="ft-pill-num">24/7</span>
              Support
            </div>
          </div>
        </div>
      </section>

     
      {/* ══ STICKY TABS ════════════════════════════════ */}
      <div className={`ft-tabs-wrap ${isScrolled ? "scrolled" : ""}`}>
        <div className="ft-tabs-inner" ref={tabsRef}>
          {CATEGORIES.map((cat, i) => (
            <button
              key={cat.key}
              className={`ft-tab ${activeTab === i ? "active" : ""}`}
              onClick={() => setActiveTab(i)}
            >
              <FontAwesomeIcon icon={cat.icon} />
              {cat.label}
              <span className="ft-tab-count">
                {featureDetails[cat.key]?.length || 0}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ══ FEATURE CONTENT ════════════════════════════ */}
      <section className="ft-content" ref={contentRef}>
        {/* Category header */}
        <div className={`ft-cat-header ft-cat-${activeCat.color}`} key={activeTab + "-header"}>
          <div className="ft-cat-icon-wrap">
            <FontAwesomeIcon icon={activeCat.cardIcon} />
          </div>
          <div>
            <h2>{activeCat.key}</h2>
            <p>{activeCat.desc}</p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="ft-bento" key={activeTab}>
          {activeFeatures.map((item, i) => (
            <div
              key={`${activeTab}-${i}`}
              className={`ft-card ${cardColor} ${i === 0 ? "ft-card-hero" : ""}`}
            >
              <span className="ft-card-num">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="ft-card-icon">
                <FontAwesomeIcon
                  icon={i === 0 ? faStar : faCircleCheck}
                />
              </div>
              <h3 className="ft-card-title">{item.title}</h3>
              <ul className="ft-card-desc">
                {item.description.map((desc, j) => (
                  <li key={j}>{desc}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════ */}
      <section className="ft-cta ft-reveal">
        <h2>
          Ready to Transform Your Business?
        </h2>
        <p>
          Start using Auditus-iERP today and experience the power of all these
          features working together.
        </p>
        <div className="ft-cta-btns">
          <Link to="/Demo" className="ft-cta-primary">
            <FontAwesomeIcon icon={faRocket} />
            Start Free Trial
          </Link>
          <Link to="/Demo" className="ft-cta-secondary">
            <FontAwesomeIcon icon={faArrowRight} />
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Features;